// SystemSettings.js — 5th tab in SystemAdmin compartment.
// Built 2026-05-05 as SS-4. See plans/SYSTEM_SETTINGS_PLAN.md.
// Backend contract: /sysadmin/settings (GET, GET /{key}, PATCH /{key},
// POST /{key}/reveal, POST /{key}/factory-reset). Permissions:
//   view_sysadmin_panel — list/get
//   edit_sysadmin_panel — patch/reveal/reset
import React, { useState, useEffect, useCallback } from "react";
import { apiFetch, can } from "../api";

// Schedules are read-only in V1 (Q9 in SS-1). Edits in V2 will write back to systemd.
const READONLY_CATEGORIES = new Set(["Schedules"]);

// JSON-stringify a value for an editable text input. For strings we want the
// raw string (no quotes), for everything else we want valid JSON the user can edit.
function valueToInputString(value, valueType) {
  if (value === null || value === undefined) return "";
  if (valueType === "string" || valueType === "url" || valueType === "cron") return String(value);
  if (valueType === "int") return String(value);
  if (valueType === "bool") return value ? "true" : "false";
  if (valueType === "json") return JSON.stringify(value, null, 2);
  return JSON.stringify(value);
}

// Parse user input back to a value. Returns {ok, value, error}.
function parseInputString(raw, valueType) {
  try {
    if (valueType === "string" || valueType === "url" || valueType === "cron" || valueType === "password") {
      return { ok: true, value: raw };
    }
    if (valueType === "int") {
      const n = parseInt(raw, 10);
      if (Number.isNaN(n)) return { ok: false, error: "Must be an integer" };
      return { ok: true, value: n };
    }
    if (valueType === "bool") return { ok: true, value: raw === "true" || raw === true };
    if (valueType === "json") return { ok: true, value: JSON.parse(raw) };
    return { ok: true, value: raw };
  } catch (e) {
    return { ok: false, error: `Invalid: ${e.message}` };
  }
}

function formatTimestamp(iso) {
  if (!iso) return "never";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

// Brand assets manager (2026-07-01) — the SINGLE editor for the logo + letterhead.
// The raw report.brand.logo_path / letterhead_path settings are hidden (is_visible=
// false) so there's no conflicting second way to set them.
function BrandAssetSlot({ target, title, note, url, canEdit, onChanged }) {
  const [src, setSrc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => {
    let alive = true, obj = null;
    (async () => {
      if (!url) { setSrc(null); return; }
      try { const res = await apiFetch(url, { raw: true }); obj = URL.createObjectURL(await res.blob()); if (alive) setSrc(obj); }
      catch { if (alive) setSrc(null); }
    })();
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [url]);
  const onUpload = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true); setMsg(null);
    try {
      const fd = new FormData(); fd.append("file", f); fd.append("target", target);
      await apiFetch("/sysadmin/branding/logo", { method: "POST", raw: true, body: fd });
      setMsg({ type: "ok", text: "Updated. Now used in " + (target === "logo" ? "email signatures" : "report letterheads") + "." });
      if (onChanged) onChanged();
    } catch (err) {
      let d = "Upload failed."; try { d = JSON.parse(err.message).detail || d; } catch {}
      setMsg({ type: "err", text: d });
    }
    setBusy(false);
  };
  const wide = target === "letterhead";
  return (
    <div style={{ flex: 1, minWidth: 260 }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>{title}</div>
      <div style={{ fontSize: 11.5, color: "#64748b", marginBottom: 8 }}>{note}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ width: wide ? 180 : 88, height: 88, border: "1px solid #e2e8f0", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: "#fff", flexShrink: 0 }}>
          {src ? <img src={src} alt={title} style={{ maxWidth: wide ? 168 : 78, maxHeight: 78, objectFit: "contain" }} />
               : <span style={{ fontSize: 11, color: "#94a3b8" }}>none</span>}
        </div>
        <div>
          {canEdit ? (
            <label className="btn btn-outline btn-sm" style={{ cursor: busy ? "default" : "pointer" }}>
              {busy ? "Uploading…" : "Upload"}
              <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={onUpload} disabled={busy} style={{ display: "none" }} />
            </label>
          ) : <span style={{ fontSize: 12, color: "#94a3b8" }}>read-only</span>}
          {msg && <div style={{ marginTop: 8, fontSize: 12, color: msg.type === "ok" ? "#166534" : "#b91c1c", maxWidth: 220 }}>{msg.text}</div>}
        </div>
      </div>
    </div>
  );
}

function BrandLogoUploader() {
  const [b, setB] = useState(null);
  const canEdit = can("edit_sysadmin_panel");
  const load = () => apiFetch("/sysadmin/branding").then(setB).catch(() => setB({}));
  useEffect(() => { load(); }, []);
  return (
    <div style={{ border: "1px solid var(--border,#e2e8f0)", borderRadius: 10, padding: 16, marginBottom: 20, background: "var(--surface2,#fff)" }}>
      <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>Brand assets</div>
      <div style={{ fontSize: 12.5, color: "#475569", marginBottom: 14 }}>
        The single place to manage brand images. Raster only (PNG/JPG/WEBP/GIF) — SVG isn't supported in email.
      </div>
      <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
        <BrandAssetSlot target="logo" title="Logo / mark" note="Square-ish; used in email signatures (and where a compact mark is shown)." url={b && b.logo_url} canEdit={canEdit} onChanged={load} />
        <BrandAssetSlot target="letterhead" title="Report letterhead banner" note="Wide wordmark banner across the top of report PDFs." url={b && b.letterhead_url} canEdit={canEdit} onChanged={load} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────
export default function SystemSettings() {
  const [data, setData] = useState(null);              // {categories: [...]}
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});        // category name → bool
  const [drafts, setDrafts] = useState({});            // key → {value, error}
  const [saving, setSaving] = useState({});            // key → bool
  const [revealed, setRevealed] = useState({});        // key → plaintext (after reveal)
  const [restartHints, setRestartHints] = useState([]); // [{key, target, hint}]
  const canEdit = can("edit_sysadmin_panel");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await apiFetch("/sysadmin/settings");
      setData(resp);
      // Default: expand first category, collapse rest
      const exp = {};
      (resp.categories || []).forEach((c, i) => { exp[c.name] = i === 0; });
      setExpanded(exp);
      setError(null);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const setDraft = (key, value) => setDrafts(prev => ({ ...prev, [key]: { value, error: null } }));
  const clearDraft = (key) => setDrafts(prev => { const n = { ...prev }; delete n[key]; return n; });

  // ───── Save ─────
  const handleSave = async (setting) => {
    const draft = drafts[setting.key];
    if (!draft) return;
    const parsed = parseInputString(draft.value, setting.value_type);
    if (!parsed.ok) {
      setDrafts(prev => ({ ...prev, [setting.key]: { ...draft, error: parsed.error } }));
      return;
    }
    setSaving(prev => ({ ...prev, [setting.key]: true }));
    try {
      const resp = await apiFetch(`/sysadmin/settings/${encodeURIComponent(setting.key)}`, {
        method: "PATCH",
        body: { value: parsed.value },
      });
      // Add restart hint banner if needed
      if (resp.requires_restart) {
        setRestartHints(prev => {
          const filtered = prev.filter(h => h.key !== setting.key);
          return [...filtered, {
            key: setting.key,
            target: resp.restart_target,
            hint: resp.restart_command_hint,
          }];
        });
      }
      clearDraft(setting.key);
      // Clear any revealed plaintext for this key (it's stale now)
      setRevealed(prev => { const n = { ...prev }; delete n[setting.key]; return n; });
      await reload();
    } catch (e) {
      setDrafts(prev => ({ ...prev, [setting.key]: { ...draft, error: String(e.message || e) } }));
    } finally {
      setSaving(prev => { const n = { ...prev }; delete n[setting.key]; return n; });
    }
  };

  // ───── Reveal ─────
  const handleReveal = async (setting) => {
    if (!canEdit) return;
    if (!window.confirm(
      `Reveal the plaintext value for "${setting.label}"?\n\n` +
      `This action is logged in the audit trail.`
    )) return;
    try {
      const resp = await apiFetch(`/sysadmin/settings/${encodeURIComponent(setting.key)}/reveal`, {
        method: "POST",
      });
      setRevealed(prev => ({ ...prev, [setting.key]: resp.value || "" }));
    } catch (e) {
      alert(`Reveal failed: ${e.message || e}`);
    }
  };

  const handleHideReveal = (key) => {
    setRevealed(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  // ───── Factory reset ─────
  const handleFactoryReset = async (setting) => {
    if (!canEdit) return;
    const msg = setting.is_secret
      ? `Clear the value for "${setting.label}"? (Secrets cannot be reset to default — they become unset.)`
      : `Reset "${setting.label}" to its default value (${valueToInputString(setting.default_value, setting.value_type) || "empty"})?`;
    if (!window.confirm(msg)) return;
    setSaving(prev => ({ ...prev, [setting.key]: true }));
    try {
      const resp = await apiFetch(`/sysadmin/settings/${encodeURIComponent(setting.key)}/factory-reset`, {
        method: "POST",
      });
      if (resp.requires_restart) {
        setRestartHints(prev => {
          const filtered = prev.filter(h => h.key !== setting.key);
          return [...filtered, { key: setting.key, target: resp.restart_target, hint: null }];
        });
      }
      clearDraft(setting.key);
      setRevealed(prev => { const n = { ...prev }; delete n[setting.key]; return n; });
      await reload();
    } catch (e) {
      alert(`Reset failed: ${e.message || e}`);
    } finally {
      setSaving(prev => { const n = { ...prev }; delete n[setting.key]; return n; });
    }
  };

  // ───── Render helpers ─────
  const renderInput = (setting, isReadonly) => {
    const draft = drafts[setting.key];
    const currentValue = draft !== undefined ? draft.value : valueToInputString(setting.value, setting.value_type);

    if (setting.is_secret) {
      const isRevealed = revealed[setting.key] !== undefined;
      if (isRevealed) {
        return (
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              type="text"
              value={revealed[setting.key]}
              readOnly
              style={inputStyle}
            />
            <button onClick={() => handleHideReveal(setting.key)} style={smallBtn}>Hide</button>
          </div>
        );
      }
      // Secret, not revealed: show masked + edit field
      return (
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input
            type="password"
            placeholder={setting.has_value ? "•••••••• (set)" : "(unset)"}
            value={draft !== undefined ? draft.value : ""}
            onChange={e => setDraft(setting.key, e.target.value)}
            disabled={isReadonly || !canEdit}
            style={inputStyle}
          />
          {setting.has_value && canEdit && (
            <button onClick={() => handleReveal(setting)} style={smallBtn}>Reveal</button>
          )}
        </div>
      );
    }

    if (setting.value_type === "bool") {
      return (
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <input
            type="checkbox"
            checked={currentValue === "true" || currentValue === true}
            onChange={e => setDraft(setting.key, e.target.checked ? "true" : "false")}
            disabled={isReadonly || !canEdit}
          />
          <span style={{ fontSize: 13 }}>{currentValue === "true" || currentValue === true ? "Enabled" : "Disabled"}</span>
        </label>
      );
    }

    if (setting.value_type === "json") {
      return (
        <textarea
          value={currentValue}
          onChange={e => setDraft(setting.key, e.target.value)}
          disabled={isReadonly || !canEdit}
          rows={Math.min(10, (currentValue.match(/\n/g) || []).length + 2)}
          style={{ ...inputStyle, fontFamily: "monospace", fontSize: 12 }}
        />
      );
    }

    return (
      <input
        type={setting.value_type === "int" ? "number" : "text"}
        value={currentValue}
        onChange={e => setDraft(setting.key, e.target.value)}
        disabled={isReadonly || !canEdit}
        style={inputStyle}
      />
    );
  };

  const renderSettingRow = (setting, isReadonly) => {
    const draft = drafts[setting.key];
    const isDirty = draft !== undefined;
    const isSaving = saving[setting.key];
    return (
      <div key={setting.key} style={rowStyle}>
        <div style={{ flex: "1 1 35%", minWidth: 240 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            {setting.label}
            {setting.is_secret && <span style={badgeStyle("#dc2626")}>SECRET</span>}
            {setting.requires_restart && <span style={badgeStyle("#d97706")}>RESTART</span>}
            {isReadonly && <span style={badgeStyle("#6b7280")}>READ-ONLY</span>}
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
            <code>{setting.key}</code>
          </div>
          {setting.description && (
            <div style={{ fontSize: 12, color: "#475569", marginTop: 4 }}>{setting.description}</div>
          )}
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
            Last changed: {setting.last_changed_by_username || "—"} @ {formatTimestamp(setting.last_changed_at)}
          </div>
        </div>
        <div style={{ flex: "1 1 50%", minWidth: 280, display: "flex", flexDirection: "column", gap: 6 }}>
          {renderInput(setting, isReadonly)}
          {draft && draft.error && (
            <div style={{ color: "#dc2626", fontSize: 12 }}>{draft.error}</div>
          )}
          {isDirty && !isReadonly && canEdit && (
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={() => handleSave(setting)} disabled={isSaving} style={primaryBtn}>
                {isSaving ? "Saving…" : "Save"}
              </button>
              <button onClick={() => clearDraft(setting.key)} disabled={isSaving} style={smallBtn}>Cancel</button>
            </div>
          )}
          {!isReadonly && canEdit && !isDirty && (
            <div>
              <button onClick={() => handleFactoryReset(setting)} disabled={isSaving}
                style={{ ...linkBtn, fontSize: 11 }}>
                {setting.is_secret ? "Clear value" : "Reset to default"}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCategory = (category) => {
    const isOpen = expanded[category.name];
    const isReadonly = READONLY_CATEGORIES.has(category.name);
    return (
      <div key={category.name} style={categoryStyle}>
        <div
          onClick={() => setExpanded(prev => ({ ...prev, [category.name]: !prev[category.name] }))}
          style={categoryHeaderStyle}
        >
          <span style={{ fontWeight: 600 }}>
            {isOpen ? "▾" : "▸"} {category.name}
          </span>
          <span style={{ fontSize: 12, color: "#64748b" }}>
            {category.settings.length} setting{category.settings.length === 1 ? "" : "s"}
            {isReadonly && " · read-only"}
          </span>
        </div>
        {isOpen && (
          <div style={{ padding: "0 16px 16px" }}>
            {isReadonly && (
              <div style={readonlyNoticeStyle}>
                These values reflect what's configured in systemd timers on the droplet. Changes
                here are not yet wired back — edit the timer files directly via SSH for now.
              </div>
            )}
            {category.settings.map(s => renderSettingRow(s, isReadonly))}
          </div>
        )}
      </div>
    );
  };

  // ───── Main render ─────
  if (loading) return <div style={{ padding: 20 }}>Loading settings…</div>;
  if (error)   return <div style={{ padding: 20, color: "#dc2626" }}>Error: {error}</div>;
  if (!data || !data.categories) return <div style={{ padding: 20 }}>No settings found.</div>;

  return (
    <div style={{ padding: 16, maxWidth: 1100 }}>
      <h2 style={{ marginTop: 0 }}>System Settings</h2>
      <p style={{ color: "#475569", fontSize: 13 }}>
        Configurable values for integrations, authentication, limits, and feature flags.
        Settings marked <span style={badgeStyle("#dc2626", true)}>SECRET</span> are encrypted at rest.
        Settings marked <span style={badgeStyle("#d97706", true)}>RESTART</span> require restarting the
        affected service after save.
      </p>

      <BrandLogoUploader />

      <CustomListsManager canEdit={canEdit} />

      {restartHints.length > 0 && (
        <div style={restartBannerStyle}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>⚠ Restart required for these changes to take effect:</div>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {restartHints.map(h => (
              <li key={h.key} style={{ fontSize: 13 }}>
                <code>{h.key}</code> → <code>{h.hint || `restart ${h.target}`}</code>
              </li>
            ))}
          </ul>
          <button onClick={() => setRestartHints([])} style={{ ...smallBtn, marginTop: 8 }}>Dismiss</button>
        </div>
      )}

      {!canEdit && (
        <div style={{ background: "#fef3c7", border: "1px solid #f59e0b", padding: 10, borderRadius: 4, marginBottom: 12, fontSize: 13 }}>
          You have view-only access. Editing requires the <code>edit_sysadmin_panel</code> capability.
        </div>
      )}

      {data.categories.map(renderCategory)}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Custom Lists (CUSTOM-LISTS-1, 2026-07-09) — admin-managed value lists with
// an employee propose → approve workflow. First list: Employee Specialties
// (feeds profile tags → the public website's Our People directory).
// ─────────────────────────────────────────────────────────────────────────
function CustomListsManager({ canEdit }) {
  const [lists, setLists] = useState(null);
  const [openKey, setOpenKey] = useState(null);
  const [items, setItems] = useState([]);
  const [newVal, setNewVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(null), 5000); };
  const loadLists = useCallback(async () => {
    try { setLists((await apiFetch("/admin/custom-lists")).lists || []); }
    catch (e) { setLists([]); }
  }, []);
  const loadItems = useCallback(async (key) => {
    try { setItems((await apiFetch(`/custom-lists/${key}/items?include=all`)).items || []); }
    catch (e) { setItems([]); }
  }, []);
  useEffect(() => { loadLists(); }, [loadLists]);
  useEffect(() => { if (openKey) loadItems(openKey); }, [openKey, loadItems]);

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); await loadItems(openKey); await loadLists(); }
    catch (e) { flash(String(e.message || e).slice(0, 180)); }
    finally { setBusy(false); }
  };
  const decide = (id, verdict) => act(() => apiFetch(`/admin/custom-lists/items/${id}/${verdict}`, { method: "POST" }));
  const setStatus = (id, status) => act(() => apiFetch(`/admin/custom-lists/items/${id}`, { method: "PATCH", body: { status } }));
  const addValue = () => {
    const value = newVal.trim();
    if (!value) return;
    act(async () => { await apiFetch(`/admin/custom-lists/${openKey}/items`, { method: "POST", body: { value } }); setNewVal(""); });
  };

  if (!lists) return null;
  const statusBadge = (s) =>
    s === "proposed" ? <span style={badgeStyle("#d97706", true)}>PENDING</span>
      : s === "retired" ? <span style={badgeStyle("#64748b", true)}>RETIRED</span>
      : <span style={badgeStyle("#15803d", true)}>ACTIVE</span>;

  return (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: 6, padding: 14, marginBottom: 16, background: "#fff" }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Custom Lists</div>
      <p style={{ color: "#475569", fontSize: 13, margin: "0 0 10px" }}>
        Managed value lists used across Atticus (dropdowns, tags). Employees can propose new values
        from their pickers; proposals wait here for approval. Approved specialty values become
        selectable on profiles and — once a profile publish is approved — appear on the company website.
      </p>
      {msg && <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 4, padding: 8, fontSize: 13, marginBottom: 8 }}>{msg}</div>}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        {lists.map(l => (
          <button key={l.key} style={{ ...smallBtn, ...(openKey === l.key ? { background: "#1e3a8a", color: "#fff" } : {}) }}
                  onClick={() => setOpenKey(openKey === l.key ? null : l.key)}>
            {l.label} · {l.active_count} active{l.pending_count > 0 ? ` · ${l.pending_count} pending ⚠` : ""}
          </button>
        ))}
      </div>
      {openKey && (
        <div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <tbody>
              {items.map(i => (
                <tr key={i.id} style={{ borderTop: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "6px 8px" }}>{i.value}</td>
                  <td style={{ padding: "6px 8px" }}>{statusBadge(i.status)}</td>
                  <td style={{ padding: "6px 8px", color: "#64748b" }}>
                    {i.status === "proposed" && i.proposed_by_name ? `proposed by ${i.proposed_by_name}` : ""}
                  </td>
                  <td style={{ padding: "6px 8px", textAlign: "right", whiteSpace: "nowrap" }}>
                    {canEdit && i.status === "proposed" && (
                      <>
                        <button style={{ ...smallBtn, marginRight: 6, color: "#15803d", borderColor: "#86efac" }}
                                disabled={busy} onClick={() => decide(i.id, "approve")}>Approve</button>
                        <button style={{ ...smallBtn, color: "#dc2626", borderColor: "#fca5a5" }}
                                disabled={busy} onClick={() => decide(i.id, "reject")}>Reject</button>
                      </>
                    )}
                    {canEdit && i.status === "active" && (
                      <button style={smallBtn} disabled={busy} onClick={() => setStatus(i.id, "retired")}>Retire</button>
                    )}
                    {canEdit && i.status === "retired" && (
                      <button style={smallBtn} disabled={busy} onClick={() => setStatus(i.id, "active")}>Reactivate</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {canEdit && (
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <input style={{ ...inputStyle, maxWidth: 320 }} placeholder="Add a value…" value={newVal}
                     onChange={e => setNewVal(e.target.value)}
                     onKeyDown={e => { if (e.key === "Enter") addValue(); }} />
              <button style={primaryBtn} disabled={busy || !newVal.trim()} onClick={addValue}>Add</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Inline styles (matching the navy/blue palette used elsewhere)
// ─────────────────────────────────────────────────────────────────────────
const inputStyle = {
  padding: "6px 10px",
  border: "1px solid #cbd5e1",
  borderRadius: 4,
  fontSize: 13,
  width: "100%",
  boxSizing: "border-box",
};
const primaryBtn = {
  padding: "6px 12px",
  background: "#1e3a8a",
  color: "#fff",
  border: "none",
  borderRadius: 4,
  fontSize: 12,
  cursor: "pointer",
};
const smallBtn = {
  padding: "4px 10px",
  background: "#fff",
  color: "#1e3a8a",
  border: "1px solid #cbd5e1",
  borderRadius: 4,
  fontSize: 12,
  cursor: "pointer",
};
const linkBtn = {
  background: "none",
  border: "none",
  color: "#1e3a8a",
  textDecoration: "underline",
  cursor: "pointer",
  padding: 0,
};
const categoryStyle = {
  border: "1px solid #e2e8f0",
  borderRadius: 6,
  marginBottom: 10,
  background: "#fff",
};
const categoryHeaderStyle = {
  padding: "12px 16px",
  cursor: "pointer",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  background: "#f8fafc",
  borderRadius: 6,
};
const rowStyle = {
  display: "flex",
  gap: 16,
  padding: "12px 0",
  borderBottom: "1px solid #f1f5f9",
  alignItems: "flex-start",
  flexWrap: "wrap",
};
const readonlyNoticeStyle = {
  background: "#fef3c7",
  border: "1px solid #fcd34d",
  padding: "8px 12px",
  borderRadius: 4,
  fontSize: 12,
  color: "#78350f",
  margin: "12px 0",
};
const restartBannerStyle = {
  background: "#fff7ed",
  border: "1px solid #fb923c",
  padding: 12,
  borderRadius: 6,
  marginBottom: 16,
  color: "#7c2d12",
};
function badgeStyle(color, inline = false) {
  return {
    display: inline ? "inline-block" : "inline-block",
    background: color,
    color: "#fff",
    fontSize: 9,
    fontWeight: 700,
    padding: "1px 5px",
    borderRadius: 3,
    marginLeft: 6,
    verticalAlign: "middle",
  };
}
